// src/pages/Finance.jsx — Centre de pilotage financier
// Flux : Finance.jsx → adminPaiement.js → adminHttp.js → Worker Admin → Worker Paiement
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  LayoutDashboard,
  CreditCard,
  Wallet,
  BarChart3,
  RefreshCw,
  Activity,
  Search,
  Filter,
  Eye,
  X,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  User,
} from 'lucide-react';
import {
  PageHeader,
  StatusBadge,
  DonutChart,
  HorizontalBars,
  AreaChart,
  EmptyState,
} from '../components/ui/UIComponents';
import {
  getPaiements,
  getRetraits,
  traiterRetrait,
  getSupervisionIndicateurs,
  lancerReconciliation,
  getJournalPaiements,
} from '../services/adminPaiement';
import { useI18n } from '../i18n/useI18n';

const CHART = ['#10b981', '#f59e0b', '#f43f5e', '#6366f1', '#0ea5e9', '#8b5cf6'];

const TABS = [
  { id: 'vue', label: 'Vue financière', icon: LayoutDashboard },
  { id: 'paiements', label: 'Paiements', icon: CreditCard },
  { id: 'retraits', label: 'Retraits', icon: Wallet },
  { id: 'supervision', label: 'Supervision', icon: BarChart3 },
  { id: 'reconciliation', label: 'Réconciliation', icon: RefreshCw },
  { id: 'journal', label: 'Journal financier', icon: Activity },
];

/** Statuts proposés dans l’UI — le backend reste la source de vérité des transitions. */
const RETRAIT_STATUTS_UI = ['demande', 'validee', 'envoyee', 'payee', 'rejetee'];

function money(n, d = 'XOF') {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return '—';
  return `${num.toLocaleString('fr-FR')} ${d}`;
}

function dt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString('fr-FR');
  } catch {
    return String(iso);
  }
}

function display(v) {
  if (v === undefined || v === null || v === '') return '—';
  return v;
}

function pick(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

function extractList(response, keys) {
  if (Array.isArray(response)) return response;
  if (!response || typeof response !== 'object') return [];
  for (const key of keys) {
    if (Array.isArray(response[key])) return response[key];
  }
  return [];
}

function payKey(s) {
  const v = (s || '').toString().toLowerCase();
  if (['confirme', 'confirmed', 'success', 'paye', 'paid', 'valide'].includes(v)) return 'ACTIF';
  if (['en_attente', 'pending', 'attente'].includes(v)) return 'EN_ATTENTE';
  if (['echec', 'failed', 'echoue', 'erreur', 'error'].includes(v)) return 'ERREUR';
  return 'INACTIF';
}

function wKey(s) {
  const v = (s || '').toString().toLowerCase();
  return (
    {
      demande: 'EN_ATTENTE',
      validee: 'ACTIF',
      envoyee: 'PREPARATION',
      payee: 'ACTIF',
      rejetee: 'ERREUR',
    }[v] || 'INACTIF'
  );
}

function VBars({ items = [], maxHint }) {
  if (!items.length) {
    return <EmptyState title="Aucune donnée" message="Données insuffisantes pour afficher la répartition." />;
  }
  const max = maxHint || Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="flex items-end justify-between gap-2 h-40 px-1">
      {items.map((it) => (
        <div key={it.label} className="flex-1 flex flex-col items-center gap-1 min-w-0">
          <span className="text-[10px] font-bold text-slate-600">{it.value}</span>
          <div
            className="w-full max-w-[48px] rounded-t-lg transition-all duration-700"
            style={{
              height: `${Math.max(8, Math.round((it.value / max) * 100))}%`,
              background: it.color || '#4f46e5',
            }}
            title={`${it.label}: ${it.value}`}
          />
          <span className="text-[10px] text-slate-500 truncate w-full text-center">{it.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function Finance() {
  const { t } = useI18n();
  const [tab, setTab] = useState('vue');
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [indicators, setIndicators] = useState(null);
  const [activity, setActivity] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [sectionErrors, setSectionErrors] = useState({});
  const [paySearch, setPaySearch] = useState('');
  const [payStatus, setPayStatus] = useState('');
  const [payPlatform, setPayPlatform] = useState('');
  const [wStatus, setWStatus] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmRecon, setConfirmRecon] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [paySeriesMode, setPaySeriesMode] = useState('count');
  const [reconRunning, setReconRunning] = useState(false);

  const showToast = (type, message) => setToast({ type, message });

  const loadAll = useCallback(async () => {
    setLoading(true);
    const errors = {};

    const [pRes, wRes, indRes, jRes] = await Promise.allSettled([
      getPaiements(),
      getRetraits(),
      getSupervisionIndicateurs(),
      getJournalPaiements(),
    ]);

    if (pRes.status === 'fulfilled') {
      setPayments(extractList(pRes.value, ['paiements', 'payments', 'data', 'items']));
    } else {
      setPayments([]);
      errors.paiements = pRes.reason?.message || 'Erreur chargement paiements';
    }

    if (wRes.status === 'fulfilled') {
      setWithdrawals(extractList(wRes.value, ['retraits', 'withdrawals', 'data', 'items']));
    } else {
      setWithdrawals([]);
      errors.retraits = wRes.reason?.message || 'Erreur chargement retraits';
    }

    if (indRes.status === 'fulfilled') {
      const raw = indRes.value;
      const ind =
        raw?.indicateurs && typeof raw.indicateurs === 'object'
          ? raw.indicateurs
          : raw && typeof raw === 'object' && !Array.isArray(raw)
            ? raw
            : null;
      setIndicators(ind);
    } else {
      setIndicators(null);
      errors.supervision = indRes.reason?.message || 'Erreur chargement supervision';
    }

    if (jRes.status === 'fulfilled') {
      setActivity(
        extractList(jRes.value, ['journal', 'activity', 'activites', 'entries', 'data', 'items'])
      );
    } else {
      setActivity([]);
      errors.journal = jRes.reason?.message || 'Erreur chargement journal';
    }

    setSectionErrors(errors);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const payId = (p) => pick(p, ['idTransaction', 'id_transaction', 'id', 'reference']);
  const payDsi = (p) => pick(p, ['identifiantDsi', 'identifiant_dsi', 'dsi', 'membre']);
  const payMontant = (p) => pick(p, ['montant', 'amount', 'montantFcfa', 'montant_fcfa']);
  const payDevise = (p) => pick(p, ['devise', 'currency']) || 'XOF';
  const payStatut = (p) => pick(p, ['statut', 'status']);
  const payPlateforme = (p) => pick(p, ['plateforme', 'platform', 'provider']);
  const payDate = (p) =>
    pick(p, ['dateCreation', 'date_creation', 'created_at', 'createdAt', 'date', 'date_paiement']);
  const payConfirm = (p) =>
    pick(p, ['dateConfirmation', 'date_confirmation', 'confirmed_at', 'confirmedAt']);
  const payType = (p) => pick(p, ['typePaiement', 'type_paiement', 'type']);
  const payRef = (p) => pick(p, ['referenceExterne', 'reference_externe', 'reference']);
  const payProduit = (p) => pick(p, ['produitId', 'produit_id', 'productId', 'produit']);

  const wId = (w) => pick(w, ['idRetrait', 'id_retrait', 'id']);
  const wDsi = (w) => pick(w, ['identifiantDsi', 'identifiant_dsi', 'dsi', 'membre']);
  const wStatut = (w) => pick(w, ['statut', 'status']);
  const wDate = (w) => pick(w, ['dateDemande', 'date_demande', 'created_at', 'createdAt', 'date']);
  const wMontantDsi = (w) => pick(w, ['montantDsiDemande', 'montant_dsi_demande', 'montantDsi']);
  const wMontantNet = (w) => pick(w, ['montantDsiNet', 'montant_dsi_net']);
  const wFcfa = (w) => pick(w, ['montantFcfa', 'montant_fcfa', 'montant']);
  const wMoyen = (w) => pick(w, ['moyenPaiement', 'moyen_paiement', 'moyen']);
  const wPays = (w) => pick(w, ['pays', 'country']);
  const wContrib = (w) => pick(w, ['contributionStabiliteDsi', 'contribution_stabilite_dsi']);

  const payByStatus = useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      const s = (payStatut(p) || 'inconnu').toString();
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], i) => ({
      label,
      value,
      color:
        payKey(label) === 'ACTIF'
          ? '#10b981'
          : payKey(label) === 'EN_ATTENTE'
            ? '#f59e0b'
            : payKey(label) === 'ERREUR'
              ? '#f43f5e'
              : CHART[i % CHART.length],
    }));
  }, [payments]);

  const wByStatus = useMemo(() => {
    const map = {};
    withdrawals.forEach((w) => {
      const s = (wStatut(w) || 'inconnu').toString();
      map[s] = (map[s] || 0) + 1;
    });
    return Object.entries(map).map(([label, value], i) => ({
      label,
      value,
      color: CHART[i % CHART.length],
    }));
  }, [withdrawals]);

  const payDonut = useMemo(
    () => payByStatus.map((x) => ({ label: x.label, value: x.value, color: x.color })),
    [payByStatus]
  );
  const wDonut = useMemo(
    () => wByStatus.filter((x) => x.value > 0).map((x) => ({ label: x.label, value: x.value, color: x.color })),
    [wByStatus]
  );

  const platformVolume = useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      const pl = payPlateforme(p);
      if (!pl) return;
      map[pl] = (map[pl] || 0) + (Number(payMontant(p)) || 0);
    });
    return Object.entries(map).map(([label, value]) => ({ label, value, color: 'bg-indigo-500' }));
  }, [payments]);

  const typeVolume = useMemo(() => {
    const map = {};
    payments.forEach((p) => {
      const tp = payType(p);
      if (!tp) return;
      map[tp] = (map[tp] || 0) + (Number(payMontant(p)) || 0);
    });
    return Object.entries(map).map(([label, value]) => ({ label, value, color: 'bg-violet-500' }));
  }, [payments]);

  const payTimeline = useMemo(() => {
    const buckets = {};
    payments.forEach((p) => {
      const raw = payDate(p);
      if (!raw) return;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      if (!buckets[key]) buckets[key] = { count: 0, volume: 0 };
      buckets[key].count += 1;
      buckets[key].volume += Number(payMontant(p)) || 0;
    });
    const keys = Object.keys(buckets).sort();
    return {
      labels: keys,
      series: keys.map((k) => (paySeriesMode === 'volume' ? buckets[k].volume : buckets[k].count)),
    };
  }, [payments, paySeriesMode]);

  const wTimeline = useMemo(() => {
    const buckets = {};
    withdrawals.forEach((w) => {
      const raw = wDate(w);
      if (!raw) return;
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      buckets[key] = (buckets[key] || 0) + 1;
    });
    const keys = Object.keys(buckets).sort();
    return { labels: keys, series: keys.map((k) => buckets[k]) };
  }, [withdrawals]);

  const kpis = useMemo(() => {
    const totalPay = payments.length;
    const volumePay = payments.reduce((s, p) => s + (Number(payMontant(p)) || 0), 0);
    const byStat = {};
    payments.forEach((p) => {
      const s = (payStatut(p) || 'inconnu').toString();
      byStat[s] = (byStat[s] || 0) + 1;
    });
    const totalW = withdrawals.length;
    const counts = Object.fromEntries(wByStatus.map((x) => [x.label, x.value]));
    return { totalPay, volumePay, byStat, totalW, counts };
  }, [payments, withdrawals, wByStatus]);

  const journalCats = useMemo(() => {
    const map = {};
    activity.forEach((a) => {
      const k = pick(a, ['type', 'categorie', 'category', 'action']) || 'autre';
      map[k] = (map[k] || 0) + 1;
    });
    return Object.entries(map).map(([label, value]) => ({ label, value, color: 'bg-indigo-500' }));
  }, [activity]);

  const filteredPayments = useMemo(() => {
    return payments.filter((p) => {
      if (payStatus && String(payStatut(p)) !== payStatus) return false;
      if (payPlatform && String(payPlateforme(p)) !== payPlatform) return false;
      if (paySearch) {
        const q = paySearch.toLowerCase();
        const blob = `${payId(p) || ''} ${payDsi(p) || ''} ${payRef(p) || ''} ${payProduit(p) || ''}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [payments, paySearch, payStatus, payPlatform]);

  const filteredWithdrawals = useMemo(() => {
    return withdrawals.filter((w) => !wStatus || String(wStatut(w)) === wStatus);
  }, [withdrawals, wStatus]);

  const platforms = useMemo(
    () => [...new Set(payments.map((p) => payPlateforme(p)).filter(Boolean))],
    [payments]
  );
  const payStatusOptions = useMemo(
    () => [...new Set(payments.map((p) => payStatut(p)).filter(Boolean))],
    [payments]
  );
  const wStatusOptions = useMemo(
    () => [...new Set(withdrawals.map((w) => wStatut(w)).filter(Boolean))],
    [withdrawals]
  );

  const indicatorRows = useMemo(() => {
    if (!indicators || typeof indicators !== 'object') return [];
    const skip = new Set(['status', 'error', 'erreur', 'message']);
    return Object.entries(indicators)
      .filter(([k, v]) => !skip.has(k) && (typeof v === 'number' || typeof v === 'string' || typeof v === 'boolean'))
      .map(([k, v]) => [k, v]);
  }, [indicators]);

  const treatWithdrawal = async (w, next) => {
    const id = wId(w);
    if (!id) {
      showToast('err', 'Identifiant de retrait introuvable sur cette ligne.');
      return;
    }
    if (!next) {
      showToast('err', 'Statut cible manquant.');
      return;
    }
    setActionLoading(true);
    try {
      await traiterRetrait(id, next);
      showToast('ok', `Retrait ${id} → ${next}`);
      setSelectedWithdrawal(null);
      await loadAll();
    } catch (err) {
      showToast('err', err.message || 'Traitement refusé.');
    } finally {
      setActionLoading(false);
    }
  };

  const runRecon = async () => {
    setConfirmRecon(false);
    setReconRunning(true);
    try {
      const res = await lancerReconciliation();
      setReconciliation({
        httpStatus: 200,
        status: res?.status || 'termine',
        raw: res,
        at: new Date().toISOString(),
      });
      showToast(
        'ok',
        res?.status === 'termine' ? 'Réconciliation terminée.' : `Réconciliation : ${res?.status || 'ok'}`
      );
    } catch (err) {
      const st = err.status;
      const bodyStatus = err.body?.status;
      if (st === 409 || bodyStatus === 'deja_en_cours') {
        setReconciliation({
          httpStatus: 409,
          status: 'deja_en_cours',
          raw: err.body,
          at: new Date().toISOString(),
        });
        showToast('warn', 'Une réconciliation est déjà en cours.');
      } else if (st === 500 || bodyStatus === 'erreur') {
        setReconciliation({
          httpStatus: st || 500,
          status: 'erreur',
          raw: err.body,
          at: new Date().toISOString(),
        });
        showToast('err', err.message || 'Erreur de réconciliation.');
      } else if (st === 403) {
        showToast('err', 'Permission insuffisante pour lancer la réconciliation.');
      } else {
        showToast('err', err.message || 'Échec de la réconciliation.');
      }
    } finally {
      setReconRunning(false);
    }
  };

  const toastClass =
    toast?.type === 'err'
      ? 'bg-rose-50 border-rose-200 text-rose-800'
      : toast?.type === 'warn'
        ? 'bg-amber-50 border-amber-200 text-amber-900'
        : 'bg-emerald-50 border-emerald-200 text-emerald-800';

  return (
    <div className="space-y-6 fade-up">
      <PageHeader
        title={t('pages.finance.title')}
        subtitle="Centre de pilotage financier — paiements, retraits, supervision et réconciliation."
        actions={
          <button type="button" onClick={loadAll} className="dsi-btn-secondary text-xs" disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('common.refresh')}
          </button>
        }
      />

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[11px] text-slate-500 border border-slate-200 bg-slate-50 px-2 py-1 rounded-full">
          Fiche membre → page Membres
        </span>
      </div>

      {toast && (
        <div className={`p-3 rounded-2xl border text-xs font-bold flex justify-between ${toastClass}`}>
          <span className="flex items-center gap-2">
            {toast.type === 'err' ? <AlertTriangle size={14} /> : <CheckCircle2 size={14} />}
            {toast.message}
          </span>
          <button type="button" onClick={() => setToast(null)} className="underline">
            {t('common.close')}
          </button>
        </div>
      )}

      {Object.keys(sectionErrors).length > 0 && (
        <div className="p-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs space-y-1">
          <p className="font-bold flex items-center gap-1">
            <AlertTriangle size={14} /> Certaines sections n’ont pas pu être chargées
          </p>
          {Object.entries(sectionErrors).map(([k, msg]) => (
            <p key={k}>
              <span className="font-semibold">{k}</span> : {msg}
            </p>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1 bg-white border border-slate-200 p-1.5 rounded-2xl">
        {TABS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 ${
                tab === item.id ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <Icon size={14} /> {item.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="dsi-card p-12 text-center text-xs text-slate-500">{t('common.loading')}</div>
      ) : (
        <>
          {tab === 'vue' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ['Paiements', kpis.totalPay, sectionErrors.paiements ? 'Chargement impossible' : `${Object.keys(kpis.byStat).length} statut(s)`],
                  ['Volume listé', money(kpis.volumePay), 'Montant total des paiements listés'],
                  ['Retraits', kpis.totalW, sectionErrors.retraits ? 'Chargement impossible' : 'Total des retraits'],
                  [
                    'Retraits « demande »',
                    kpis.counts.demande ?? '—',
                    kpis.counts.demande != null ? 'En attente de traitement' : 'Aucun pour le moment',
                  ],
                ].map(([label, value, sub]) => (
                  <div key={label} className="dsi-card p-4">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">{label}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
                    <p className="text-[11px] text-slate-500 mt-1">{sub}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-1">Histogramme — paiements par statut</h3>
                  <p className="text-xs text-slate-500 mb-3">Répartition par statut</p>
                  <VBars items={payByStatus} />
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-1">Histogramme — retraits par statut</h3>
                  <p className="text-xs text-slate-500 mb-3">Répartition par statut</p>
                  <VBars items={wByStatus} />
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-3">Répartition des paiements</h3>
                  {payDonut.length ? (
                    <DonutChart segments={payDonut} centerValue={kpis.totalPay} centerLabel="Total" />
                  ) : (
                    <EmptyState title={t('common.empty')} message="Aucun paiement à répartir." />
                  )}
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-3">Répartition des retraits</h3>
                  {wDonut.length ? (
                    <DonutChart segments={wDonut} centerValue={kpis.totalW} centerLabel="Total" />
                  ) : (
                    <EmptyState title={t('common.empty')} message="Aucun retrait à répartir." />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div>
                      <h3 className="text-sm font-semibold">Évolution des paiements</h3>
                      <p className="text-xs text-slate-500">Évolution dans le temps</p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${paySeriesMode === 'count' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200'}`}
                        onClick={() => setPaySeriesMode('count')}
                      >
                        Nb transactions
                      </button>
                      <button
                        type="button"
                        className={`px-2 py-1 rounded-lg text-[10px] font-bold border ${paySeriesMode === 'volume' ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200'}`}
                        onClick={() => setPaySeriesMode('volume')}
                      >
                        Volume
                      </button>
                    </div>
                  </div>
                  {payTimeline.series.length ? (
                    <>
                      <AreaChart series={payTimeline.series} />
                      <div className="mt-2 flex justify-between text-[10px] text-slate-400 overflow-x-auto gap-1">
                        {payTimeline.labels.map((l) => (
                          <span key={l}>{l.slice(5)}</span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <EmptyState title="Données insuffisantes" message="Données insuffisantes pour afficher l’évolution." />
                  )}
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-1">Évolution des demandes de retrait</h3>
                  <p className="text-xs text-slate-500 mb-2">Évolution dans le temps</p>
                  {wTimeline.series.length ? (
                    <>
                      <AreaChart series={wTimeline.series} />
                      <div className="mt-2 flex justify-between text-[10px] text-slate-400">
                        {wTimeline.labels.map((l) => (
                          <span key={l}>{l.slice(5)}</span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <EmptyState title="Données insuffisantes" message="Données insuffisantes pour afficher l’évolution." />
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === 'paiements' && (
            <div className="space-y-4">
              {sectionErrors.paiements && (
                <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs">
                  {sectionErrors.paiements}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="dsi-card p-4">
                  <p className="text-[10px] uppercase text-slate-500 font-semibold">Total</p>
                  <p className="text-2xl font-bold mt-1">{kpis.totalPay}</p>
                </div>
                {payByStatus.slice(0, 3).map((s) => (
                  <div key={s.label} className="dsi-card p-4">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold truncate">{s.label}</p>
                    <p className="text-2xl font-bold mt-1">{s.value}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Par statut</h3>
                  <VBars items={payByStatus} />
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Volume par plateforme</h3>
                  {platformVolume.length ? (
                    <HorizontalBars rows={platformVolume} />
                  ) : (
                    <EmptyState title="—" message="Aucune répartition par plateforme disponible." />
                  )}
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Volume par type</h3>
                  {typeVolume.length ? (
                    <HorizontalBars rows={typeVolume} />
                  ) : (
                    <EmptyState title="—" message="Aucune répartition par type disponible." />
                  )}
                </div>
              </div>

              <div className="dsi-card p-4 flex flex-col lg:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    className="dsi-input pl-9"
                    placeholder="ID, DSI, référence…"
                    value={paySearch}
                    onChange={(e) => setPaySearch(e.target.value)}
                  />
                </div>
                <select className="dsi-input w-auto" value={payStatus} onChange={(e) => setPayStatus(e.target.value)}>
                  <option value="">Statut</option>
                  {payStatusOptions.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <select className="dsi-input w-auto" value={payPlatform} onChange={(e) => setPayPlatform(e.target.value)}>
                  <option value="">Plateforme</option>
                  {platforms.map((pl) => (
                    <option key={pl} value={pl}>
                      {pl}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dsi-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3">Transaction</th>
                        <th className="px-4 py-3">Membre</th>
                        <th className="px-4 py-3">Plateforme</th>
                        <th className="px-4 py-3">Montant</th>
                        <th className="px-4 py-3">Statut</th>
                        <th className="px-4 py-3">Création</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredPayments.map((p, idx) => (
                        <tr key={payId(p) || idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-xs font-semibold">{display(payId(p))}</td>
                          <td className="px-4 py-3">
                            <span className="font-medium">{display(payDsi(p))}</span>
                            <span className="block text-[10px] text-slate-400">Fiche membre → page Membres</span>
                          </td>
                          <td className="px-4 py-3">{display(payPlateforme(p))}</td>
                          <td className="px-4 py-3 font-bold">{money(payMontant(p), payDevise(p))}</td>
                          <td className="px-4 py-3">
                            <StatusBadge statusKey={payKey(payStatut(p))} text={display(payStatut(p))} />
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{dt(payDate(p))}</td>
                          <td className="px-4 py-3 text-right">
                            <button type="button" className="dsi-btn-ghost text-xs" onClick={() => setSelectedPayment(p)}>
                              <Eye size={14} /> Détail
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!filteredPayments.length && <EmptyState title="Aucun paiement" />}
              </div>
            </div>
          )}

          {tab === 'retraits' && (
            <div className="space-y-4">
              {sectionErrors.retraits && (
                <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs">
                  {sectionErrors.retraits}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {(wByStatus.length ? wByStatus : [{ label: '—', value: 0 }]).map((s) => (
                  <div key={s.label} className="dsi-card p-4">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">{s.label}</p>
                    <p className="text-2xl font-bold mt-1">{s.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Histogramme des statuts</h3>
                  <VBars items={wByStatus} />
                </div>
                <div className="dsi-card p-5">
                  <h3 className="text-sm font-semibold mb-2">Répartition</h3>
                  {wDonut.length ? (
                    <DonutChart segments={wDonut} centerValue={kpis.totalW} centerLabel="Retraits" />
                  ) : (
                    <EmptyState title="—" />
                  )}
                </div>
              </div>
              <div className="dsi-card p-4 flex gap-2 items-center">
                <Filter size={14} className="text-slate-400" />
                <select className="dsi-input w-auto" value={wStatus} onChange={(e) => setWStatus(e.target.value)}>
                  <option value="">Tous</option>
                  {(wStatusOptions.length ? wStatusOptions : RETRAIT_STATUTS_UI).map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="dsi-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3">Retrait</th>
                        <th className="px-4 py-3">Membre</th>
                        <th className="px-4 py-3">DSI dem.</th>
                        <th className="px-4 py-3">DSI net</th>
                        <th className="px-4 py-3">FCFA</th>
                        <th className="px-4 py-3">Statut</th>
                        <th className="px-4 py-3">Date</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredWithdrawals.map((w, idx) => (
                        <tr key={wId(w) || idx} className="hover:bg-slate-50">
                          <td className="px-4 py-3 font-mono text-xs font-semibold">{display(wId(w))}</td>
                          <td className="px-4 py-3">{display(wDsi(w))}</td>
                          <td className="px-4 py-3">{display(wMontantDsi(w))}</td>
                          <td className="px-4 py-3">{display(wMontantNet(w))}</td>
                          <td className="px-4 py-3 font-bold">{money(wFcfa(w))}</td>
                          <td className="px-4 py-3">
                            <StatusBadge statusKey={wKey(wStatut(w))} text={display(wStatut(w))} />
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{dt(wDate(w))}</td>
                          <td className="px-4 py-3 text-right">
                            <button type="button" className="dsi-btn-ghost text-xs" onClick={() => setSelectedWithdrawal(w)}>
                              Traiter
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!filteredWithdrawals.length && <EmptyState title="Aucun retrait" />}
                <p className="px-4 py-2 text-[10px] text-slate-500 border-t border-slate-100 bg-slate-50">
                  Le statut définitif est confirmé après traitement.
                </p>
              </div>
            </div>
          )}

          {tab === 'supervision' && (
            <div className="space-y-4">
              {sectionErrors.supervision && (
                <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs">
                  {sectionErrors.supervision}
                </div>
              )}
              {indicatorRows.length ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {indicatorRows.map(([k, v]) => (
                      <div key={k} className="dsi-card p-4">
                        <p className="text-[10px] uppercase text-slate-500 font-semibold break-all">{k}</p>
                        <p className="text-xl font-bold mt-1">
                          {typeof v === 'number' ? v.toLocaleString('fr-FR') : String(v)}
                        </p>
                      </div>
                    ))}
                  </div>
                  <div className="dsi-card p-5">
                    <h3 className="text-sm font-semibold mb-2">Indicateurs numériques</h3>
                    {indicatorRows.some(([, v]) => typeof v === 'number') ? (
                      <HorizontalBars
                        rows={indicatorRows
                          .filter(([, v]) => typeof v === 'number')
                          .map(([label, value], i) => ({
                            label,
                            value,
                            color: i % 2 === 0 ? 'bg-indigo-500' : 'bg-amber-500',
                          }))}
                      />
                    ) : (
                      <EmptyState title="—" message="Aucun indicateur numérique exploitable." />
                    )}
                  </div>
                </>
              ) : (
                <EmptyState
                  title="Aucun indicateur exploitable"
                  message={
                    sectionErrors.supervision
                      ? sectionErrors.supervision
                      : 'Aucun indicateur disponible pour le moment.'
                  }
                />
              )}
            </div>
          )}

          {tab === 'reconciliation' && (
            <div className="space-y-4">
              <div className="dsi-card p-5 space-y-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="text-amber-600" size={18} />
                  <h3 className="text-sm font-bold">Réconciliation des données</h3>
                </div>
                <p className="text-xs text-slate-500">
                  Lance une réconciliation des données financières. Cette action peut prendre quelques instants.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">Dernier statut connu</p>
                    <p className="font-bold mt-1">{reconciliation?.status || '—'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">Code</p>
                    <p className="font-bold mt-1">{reconciliation?.httpStatus ?? '—'}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                    <p className="text-[10px] uppercase text-slate-500 font-semibold">Horodatage local</p>
                    <p className="font-bold mt-1 text-xs">{dt(reconciliation?.at)}</p>
                  </div>
                </div>
                {reconciliation?.status === 'deja_en_cours' && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
                    Une réconciliation est déjà en cours. Réessayez plus tard.
                  </p>
                )}
                {reconciliation?.status === 'erreur' && (
                  <p className="text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-xl p-3">
                    Réconciliation en erreur côté serveur.
                  </p>
                )}
                {!reconciliation && (
                  <EmptyState
                    title="Aucun résultat local"
                    message="Lancez une réconciliation pour obtenir le dernier résultat."
                  />
                )}
                <button
                  type="button"
                  className="dsi-btn-primary text-xs"
                  onClick={() => setConfirmRecon(true)}
                  disabled={reconRunning}
                >
                  {reconRunning ? 'En cours…' : 'Lancer la réconciliation'}
                </button>
              </div>
            </div>
          )}

          {tab === 'journal' && (
            <div className="space-y-4">
              {sectionErrors.journal && (
                <div className="p-3 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-xs">
                  {sectionErrors.journal}
                </div>
              )}
              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold mb-2">Activité par catégorie</h3>
                {journalCats.length ? (
                  <HorizontalBars rows={journalCats} />
                ) : (
                  <EmptyState title="—" message="Aucune entrée de journal." />
                )}
              </div>
              <div className="dsi-card p-5">
                <h3 className="text-sm font-semibold mb-4">Chronologie</h3>
                {activity.length ? (
                  <div className="relative border-l-2 border-slate-200 ml-2 space-y-4">
                    {activity.map((e, idx) => {
                      const id = pick(e, ['id', 'ref', 'reference']) || idx;
                      const label =
                        pick(e, ['label', 'libelle', 'message', 'description', 'type']) || 'Événement';
                      const at = pick(e, ['at', 'date', 'created_at', 'createdAt', 'timestamp']);
                      const ref = pick(e, ['ref', 'reference', 'id']);
                      const admin = pick(e, ['admin', 'administrateur', 'user', 'acteur']);
                      return (
                        <div key={id} className="relative pl-5">
                          <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-white border-2 border-indigo-400" />
                          <p className="text-[11px] text-slate-400 font-mono">{dt(at)}</p>
                          <p className="text-sm font-semibold text-slate-900">{label}</p>
                          <p className="text-[11px] text-slate-500">
                            {[ref, admin].filter(Boolean).join(' · ') || '—'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <EmptyState title="Journal vide" message="Aucune activité à afficher pour le moment." />
                )}
              </div>
            </div>
          )}
        </>
      )}

      {selectedPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setSelectedPayment(null)} />
          <div className="relative w-full max-w-lg dsi-card p-6 space-y-2 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between">
              <h3 className="font-bold">{display(payId(selectedPayment))}</h3>
              <button type="button" onClick={() => setSelectedPayment(null)} className="p-2 hover:bg-slate-100 rounded-xl">
                <X size={18} />
              </button>
            </div>
            {[
              ['Membre', payDsi(selectedPayment)],
              ['Plateforme', payPlateforme(selectedPayment)],
              ['Réf. externe', payRef(selectedPayment)],
              ['Produit', payProduit(selectedPayment)],
              ['Montant', money(payMontant(selectedPayment), payDevise(selectedPayment))],
              ['Type', payType(selectedPayment)],
              ['Statut', payStatut(selectedPayment)],
              ['Création', dt(payDate(selectedPayment))],
              ['Confirmation', dt(payConfirm(selectedPayment))],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs border-b border-slate-100 py-2">
                <span className="text-slate-500">{k}</span>
                <span className="font-semibold">{display(v)}</span>
              </div>
            ))}
            <p className="text-[11px] text-slate-400 flex items-center gap-1 pt-2">
              <User size={12} /> Fiche membre complète → page Membres
            </p>
          </div>
        </div>
      )}

      {selectedWithdrawal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => !actionLoading && setSelectedWithdrawal(null)} />
          <div className="relative w-full max-w-lg dsi-card p-6 space-y-2 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between">
              <h3 className="font-bold">{display(wId(selectedWithdrawal))}</h3>
              <button
                type="button"
                onClick={() => setSelectedWithdrawal(null)}
                className="p-2 hover:bg-slate-100 rounded-xl"
                disabled={actionLoading}
              >
                <X size={18} />
              </button>
            </div>
            {[
              ['Membre', wDsi(selectedWithdrawal)],
              ['DSI demandé', wMontantDsi(selectedWithdrawal)],
              ['Contribution', wContrib(selectedWithdrawal)],
              ['DSI net', wMontantNet(selectedWithdrawal)],
              ['FCFA', money(wFcfa(selectedWithdrawal))],
              ['Statut actuel', wStatut(selectedWithdrawal)],
              ['Moyen', wMoyen(selectedWithdrawal)],
              ['Pays', wPays(selectedWithdrawal)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-xs border-b border-slate-100 py-2">
                <span className="text-slate-500">{k}</span>
                <span className="font-semibold">{display(v)}</span>
              </div>
            ))}
            <p className="text-[11px] text-slate-500 pt-2">
              Choisissez le nouveau statut souhaité. La confirmation dépend du résultat du traitement.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              {RETRAIT_STATUTS_UI.filter((s) => s !== String(wStatut(selectedWithdrawal) || '')).map((next) => (
                <button
                  key={next}
                  type="button"
                  className="dsi-btn-secondary text-xs"
                  disabled={actionLoading}
                  onClick={() => treatWithdrawal(selectedWithdrawal, next)}
                >
                  <ArrowRight size={12} /> {next}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {confirmRecon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setConfirmRecon(false)} />
          <div className="relative w-full max-w-md dsi-card p-6 space-y-4">
            <h3 className="font-bold">Lancer la réconciliation ?</h3>
            <p className="text-xs text-slate-600">
              Action sensible. Si une réconciliation est déjà en cours, un message vous en informera.
            </p>
            <div className="flex justify-end gap-2">
              <button type="button" className="dsi-btn-secondary text-xs" onClick={() => setConfirmRecon(false)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="dsi-btn-primary text-xs" onClick={runRecon}>
                {t('common.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
